import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { AttachmentUtils } from "../../core/utils/attachment-utils";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { SovJobEvent, SovUploadResponse, SovUploadSheet } from "../../model/maia/sov-upload";

/** Carries what the geocode step needs to call `POST /sov/{jobId}/geocode`. */
export interface MaiaReviewSubmit {
    jobId: number | null;
    totalLocations: number;
}

/**
 * MAIA wizard — Review geocoding.
 *
 * Split out of MaiaUploadStepComponent so upload and review are independent
 * steps in the flow (see maia-step-header), rather than two internal views
 * of one component. The upload step still owns the file/parse lifecycle and
 * hands this component the finished response once parsing completes.
 *
 * Design reference: docs/AI/Screenshot from 2026-08-07 23-22-40.png
 */
@Component({
    selector: "maia-review-step",
    templateUrl: "./maia-review-step.component.html",
    styleUrls: ["./maia-review-step.component.scss"],
})
export class MaiaReviewStepComponent implements OnInit, OnDestroy {
    /** The parsed upload this screen reviews. */
    @Input() uploadResponse: SovUploadResponse | null = null;

    /** File name, size, record/sheet counts — computed by the upload step. */
    @Input() fileSummary = "";

    /**
     * Row count from the job stream — the fallback source for
     * `locationsRequiringGeocoding` when an SSE-driven parse completed
     * without a `summary` object. Computed by the upload step.
     */
    @Input() jobTotalRows: number | null = null;

    /** The card's close (×) control. Reaching review always implies a file would be lost. */
    @Output() readonly closeRequested = new EventEmitter<boolean>();

    /** "Back" — return to the upload screen. */
    @Output() readonly back = new EventEmitter<void>();

    /** "Replace file" — return to the upload screen and start over. */
    @Output() readonly replaceFileRequested = new EventEmitter<void>();

    /** Submit — advances to the geocode step, carrying what it needs to call the geocode endpoint. */
    @Output() readonly submit = new EventEmitter<MaiaReviewSubmit>();

    /** Workbook types the corrected file may be brought back as — the ones upload itself accepts. */
    readonly acceptedExtensions = ".csv,.xlsx,.xls,.json";

    /**
     * Rows the backend flagged as duplicates of each other, or null until it
     * has been asked. Duplicates are flagged during parsing and never removed
     * automatically, so a count above zero is what offers the review round
     * trip below — it does not block submitting.
     */
    duplicateRowCount: number | null = null;

    /** True while the duplicate-review workbook is being prepared. */
    isDownloadingDuplicates = false;
    /** Set when the download failed. */
    duplicateDownloadError = "";

    /** True while a corrected workbook is being sent back. */
    isImportingDuplicates = false;
    /** Set when the import failed. */
    duplicateImportError = "";
    /** Confirmation shown after a successful import — the backend's own per-row summary. */
    duplicateImportMessage = "";

    @ViewChild("duplicateFileInput")
    private readonly duplicateFileInput?: ElementRef<HTMLInputElement>;

    /**
     * The job's totals re-read after a duplicate-review import, which adds and
     * removes rows. Only set on that path: on first load the `uploadResponse`
     * summary is both current and more precise (it can subtract rows that
     * already have coordinates), so it stays in charge until an import makes
     * it stale.
     *
     * The sheet list is re-read for the same reason — the backend rebuilds the
     * job's per-sheet breakdown as part of the merge
     * (SovIngestionService.updatedSheets), so "Sheets detected" would
     * otherwise keep showing the row count parsing first recorded.
     */
    private refreshedTotalRows: number | null = null;
    private refreshedSheets: SovUploadSheet[] | null = null;

    private snapshotSubscription?: Subscription;
    private downloadSubscription?: Subscription;
    private importSubscription?: Subscription;

    readonly geocodingFields = [
        { label: "ADDRESS LINE 1", value: "Street", testId: "address-line-1" },
        { label: "CITY", value: "City", testId: "city" },
        { label: "STATE", value: "State", testId: "state" },
        { label: "ZIP CODE", value: "Zip Code", testId: "zip-code" },
    ];

    constructor(private readonly maiaUploadService: MaiaUploadService) {}

    ngOnInit(): void {
        this.loadDuplicateCount(false);
    }

    ngOnDestroy(): void {
        if (this.snapshotSubscription) {
            this.snapshotSubscription.unsubscribe();
        }
        if (this.downloadSubscription) {
            this.downloadSubscription.unsubscribe();
        }
        if (this.importSubscription) {
            this.importSubscription.unsubscribe();
        }
    }

    get jobId(): number | null {
        return this.uploadResponse && this.uploadResponse.jobId !== undefined
            ? this.uploadResponse.jobId
            : null;
    }

    /** Whether this job has duplicates to review — what shows the round-trip controls. */
    get hasDuplicates(): boolean {
        return (this.duplicateRowCount || 0) > 0;
    }

    get sheets(): SovUploadSheet[] {
        if (this.refreshedSheets) {
            return this.refreshedSheets;
        }
        return this.uploadResponse && this.uploadResponse.sheets
            ? this.uploadResponse.sheets
            : [];
    }

    get skippedSheetCount(): number {
        return this.sheets.filter((sheet) => !sheet.included).length;
    }

    /**
     * Records still needing a lookup.
     *
     * Exact once a summary with real counts is present (total minus those
     * that already had coordinates). Right after upload, `summary` can
     * already be attached but still zeroed — VAPT/geocode counters start at
     * 0 and fill in as those stages run — so a present-but-zero summary is
     * treated the same as no summary: fall back to the job stream's row
     * total, and if that isn't available yet either (the synchronous-parse
     * path never streams), the per-sheet `estimatedRowCount` the upload
     * response already carries.
     */
    get locationsRequiringGeocoding(): number | null {
        // Set only by a duplicate-review import, which has just changed the row
        // count the summary below was built from.
        if (this.refreshedTotalRows !== null) {
            return this.refreshedTotalRows;
        }

        const summary = this.uploadResponse ? this.uploadResponse.summary : null;
        if (summary && summary.totalRecords > 0) {
            return Math.max(
                summary.totalRecords - summary.geocodingSkippedHadCoordinates,
                0
            );
        }
        if (this.jobTotalRows !== null) {
            return this.jobTotalRows;
        }
        if (!this.sheets.length) {
            return null;
        }
        const included = this.sheets.filter((sheet) => sheet.included);
        // -1 is the backend's "not yet known" sentinel (SovConstants.UNKNOWN_ROW_COUNT) — a real
        // per-sheet count, not zero, so summing it in would silently produce a negative total.
        if (included.some((sheet) => sheet.estimatedRowCount < 0)) {
            return null;
        }
        return included.reduce((total, sheet) => total + sheet.estimatedRowCount, 0);
    }

    /**
     * The file chip's text, with its record count kept current.
     *
     * `fileSummary` arrives already assembled, built once by whoever owned the
     * file before this screen opened — the upload step from the live File, or
     * the page from the resumed job. A duplicate-review import then changes the
     * job's row count, which leaves the "N rows" segment of that string
     * describing a file state that no longer exists.
     *
     * Rather than take the string apart by pattern, this replaces the one
     * segment it can identify: both builders join with " · " and label that
     * segment "<count> rows" (MaiaUploadStepComponent.fileSummary and
     * MaiaPageComponent.buildReviewFileSummary, which documents the shared
     * format). If no such segment is there, the original is shown untouched —
     * a chip that is one count out of date beats one this guessed at.
     */
    get displayFileSummary(): string {
        if (this.refreshedTotalRows === null || !this.fileSummary) {
            return this.fileSummary;
        }

        const parts = this.fileSummary.split(" · ");
        const index = parts.findIndex((part) => /^[\d,.\s]+rows$/.test(part));
        if (index === -1) {
            return this.fileSummary;
        }

        parts[index] = `${this.refreshedTotalRows.toLocaleString()} rows`;
        return parts.join(" · ");
    }

    /** "~N rows", or a placeholder while the backend hasn't estimated this sheet's size yet. */
    sheetRowsLabel(sheet: SovUploadSheet): string {
        return sheet.estimatedRowCount < 0 ? "Row count pending" : `~${sheet.estimatedRowCount} rows`;
    }

    requestClose(): void {
        this.closeRequested.emit(true);
    }

    /**
     * Asks the job's event stream for its duplicate count.
     *
     * `applyTotals` is false on first load — the response's own summary is
     * current then — and true after an import, where the merge has changed the
     * job's row count and sheet breakdown and this snapshot is the fresh copy
     * of both.
     */
    private loadDuplicateCount(applyTotals: boolean): void {
        const jobId = this.jobId;
        if (jobId === null) {
            return;
        }

        if (this.snapshotSubscription) {
            this.snapshotSubscription.unsubscribe();
        }

        this.snapshotSubscription = this.maiaUploadService
            .getJobEventSnapshot(jobId)
            .subscribe({
                next: (snapshot) => this.applySnapshot(snapshot, applyTotals),
                // Never surfaced: a count we couldn't read just leaves the
                // duplicates block hidden, rather than putting an error on a
                // screen whose own job parsed fine.
                error: () => undefined,
            });
    }

    private applySnapshot(snapshot: SovJobEvent, applyTotals: boolean): void {
        if (!snapshot) {
            return;
        }
        if (typeof snapshot.duplicateRowCount === "number") {
            this.duplicateRowCount = snapshot.duplicateRowCount;
        }
        if (!applyTotals) {
            return;
        }
        if (typeof snapshot.totalRows === "number") {
            this.refreshedTotalRows = snapshot.totalRows;
        }
        if (snapshot.sheets && snapshot.sheets.length) {
            this.refreshedSheets = snapshot.sheets;
        }
    }

    /** Downloads the workbook with the duplicate rows sorted to the top. */
    downloadDuplicates(): void {
        const jobId = this.jobId;
        if (jobId === null || this.isDownloadingDuplicates) {
            return;
        }

        this.isDownloadingDuplicates = true;
        this.duplicateDownloadError = "";

        this.downloadSubscription = this.maiaUploadService
            .downloadDuplicateReview(jobId)
            .subscribe({
                next: (response) => {
                    this.isDownloadingDuplicates = false;
                    if (!response.body) {
                        this.duplicateDownloadError =
                            "The download came back empty. Please try again.";
                        return;
                    }
                    AttachmentUtils.downloadFileWithName(
                        response.body as any,
                        this.maiaUploadService.resolveDownloadFileName(
                            response,
                            `sov-${jobId}-duplicate-review.xlsx`
                        )
                    );
                },
                error: () => {
                    this.isDownloadingDuplicates = false;
                    this.duplicateDownloadError =
                        "We couldn't prepare the download. Please try again.";
                },
            });
    }

    openDuplicateFilePicker(): void {
        if (this.isImportingDuplicates) {
            return;
        }
        if (this.duplicateFileInput) {
            this.duplicateFileInput.nativeElement.click();
        }
    }

    onDuplicateFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length ? input.files[0] : null;

        // Lets the same file be re-picked straight after a failed attempt.
        input.value = "";

        if (file) {
            this.importDuplicateFile(file);
        }
    }

    /**
     * Sends the corrected workbook back, then re-reads the job rather than
     * trusting the import's own message for the new state: the merge can add
     * and remove rows, so both the duplicate count and the totals this screen
     * shows have to come from the job again.
     */
    private importDuplicateFile(file: File): void {
        const jobId = this.jobId;
        if (jobId === null) {
            return;
        }

        this.isImportingDuplicates = true;
        this.duplicateImportError = "";
        this.duplicateImportMessage = "";

        this.importSubscription = this.maiaUploadService
            .importDuplicateReview(jobId, file)
            .subscribe({
                next: (response) => {
                    this.isImportingDuplicates = false;
                    this.duplicateImportMessage =
                        (response && response.message) || `${file.name} imported.`;
                    this.loadDuplicateCount(true);
                },
                error: (error) => {
                    this.isImportingDuplicates = false;
                    this.duplicateImportError = this.extractErrorMessage(error);
                },
            });
    }

    /**
     * The backend's own reason, when it sent one.
     *
     * `errors` comes first and `message` second, because on this API's error
     * body the two are not alternatives: `message` is the generic class of
     * failure ("Bad request error") while `errors` holds the sentence that
     * actually tells the user what to do — e.g. that the sheet was renamed and
     * the export needs re-downloading. Reading `message` first would reliably
     * show the useless half of a response that contained the useful one.
     *
     * `error.message` is deliberately never used: on an HttpErrorResponse that
     * is Angular's "Http failure response for <url>: 400 Bad Request".
     */
    private extractErrorMessage(error: any): string {
        const body = error && error.error;
        const fallback = "We couldn't import that file. Please check it and try again.";

        if (!body) {
            return fallback;
        }

        // A plain-text body, which some error paths send instead of JSON.
        if (typeof body === "string" && body.trim()) {
            return body.trim();
        }

        // Normally one sentence; joined rather than truncated so a response
        // that reports several problems doesn't silently hide all but the first.
        if (Array.isArray(body.errors)) {
            const reasons = body.errors
                .filter((reason: unknown) => typeof reason === "string" && reason.trim())
                .map((reason: string) => reason.trim());
            if (reasons.length) {
                return reasons.join(" ");
            }
        }

        const message = body.message || body.error;
        return (typeof message === "string" && message.trim()) || fallback;
    }

    submitForGeocoding(): void {
        this.submit.emit({
            jobId: this.uploadResponse ? this.uploadResponse.jobId : null,
            totalLocations: this.locationsRequiringGeocoding || 0,
        });
    }
}
